export interface LaunchStage {
  readonly id: string;
  readonly label: string;
  readonly status: "pending" | "running" | "done" | "skipped" | "warning" | "failed";
  readonly startedAt: string | null;
  readonly endedAt: string | null;
  readonly percent: number | null;
  readonly detail: string | null;
}

export interface WorktreeLaunchProgress {
  readonly phase: "running" | "failed" | "cancelled" | "done";
  readonly startedAt: string;
  readonly endedAt: string | null;
  readonly stages: readonly LaunchStage[];
  readonly branch: string | null;
  readonly baseBranch: string | null;
  readonly worktreePath: string | null;
  readonly terminalId: string | null;
  readonly output: readonly string[];
  readonly error: string | null;
  // A transient, non-error reason to wait (e.g. a shared runtime busy with
  // another turn). Only ever set by the synthetic pre-activity admission
  // progress in admissionLaunchProgress.ts — deriveWorktreeLaunchProgress
  // itself never sets this to anything but null.
  readonly note: string | null;
}

interface LaunchActivity {
  readonly kind: string;
  readonly createdAt: string;
  readonly payload?: unknown;
}

interface StoredLaunchActivity extends LaunchActivity {
  readonly kind: string;
}

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

const STAGES = [
  ["resolve", "Resolve base branch"],
  ["checkout", "Check out files"],
  ["dependencies", "Prepare dependencies"],
  ["setup", "Start setup script"],
  ["agent", "Start agent"],
] as const;

// Exposed so admissionLaunchProgress.ts can prepend a synthetic stage ahead
// of the same pending roadmap, before any real worktree.* activity exists.
export function pendingLaunchStages(): LaunchStage[] {
  return STAGES.map(([id, label]) => ({
    id,
    label,
    status: "pending",
    startedAt: null,
    endedAt: null,
    percent: null,
    detail: null,
  }));
}

/**
 * How a launch that never reached the agent ended: the turn's terminal state
 * on a direct route, or a replicated failure on a cloud route. `detail` is the
 * failure text shown on the panel when the provider refused to start.
 */
export type LaunchStop = {
  readonly state: "error" | "interrupted";
  readonly completedAt: string;
  readonly detail?: string;
};

export function deriveWorktreeLaunchProgress(
  activities: readonly LaunchActivity[],
  agentStartedAt: string | null,
  stopped?: LaunchStop,
): WorktreeLaunchProgress | null {
  const start = activities.findLastIndex((activity) => activity.kind === "worktree.requested");
  const requested = activities[start];
  if (!requested) return null;
  const payload = record(requested.payload);
  let result: WorktreeLaunchProgress = {
    phase: "running",
    startedAt: requested.createdAt,
    endedAt: null,
    branch: text(payload.branch),
    baseBranch: text(payload.baseBranch),
    worktreePath: null,
    terminalId: null,
    output: [],
    error: null,
    note: null,
    stages: pendingLaunchStages(),
  };
  for (const activity of activities.slice(start + 1)) {
    result = reduceLaunchActivity(result, activity);
  }
  return finishLaunch(result, payload, agentStartedAt, stopped);
}

/**
 * Reads launch state from either native activities or a durable replica that
 * was hydrated into the thread store. Replica tool calls retain their original
 * payload so this keeps both runtime routes on one progress model.
 */
export function deriveWorktreeLaunchProgressFromStoredActivities(
  activities: readonly StoredLaunchActivity[],
  agentStartedAt: string | null,
  stopped?: LaunchStop,
): WorktreeLaunchProgress | null {
  return deriveWorktreeLaunchProgress(
    activities.flatMap((activity) => storedLaunchActivity(activity)),
    agentStartedAt,
    stopped,
  );
}

function storedLaunchActivity(activity: StoredLaunchActivity): readonly LaunchActivity[] {
  if (activity.kind !== "replica.tool_call") return [activity];
  const payload = record(activity.payload).payload;
  const kind = decodeCmmdRuntimeWorktreeLaunchActivityKind(payload);
  return kind === null ? [] : [{ kind, createdAt: activity.createdAt, payload }];
}

function patchStage(
  result: WorktreeLaunchProgress,
  id: string,
  patch: Partial<LaunchStage>,
): WorktreeLaunchProgress {
  return {
    ...result,
    stages: result.stages.map((stage) => (stage.id === id ? { ...stage, ...patch } : stage)),
  };
}

function applyGitStage(
  result: WorktreeLaunchProgress,
  activity: LaunchActivity,
): WorktreeLaunchProgress {
  const data = record(activity.payload);
  const stage = result.stages.find((candidate) => candidate.id === data.stage);
  const status = data.status;
  if (!stage || (status !== "running" && status !== "done" && status !== "skipped")) return result;
  return patchStage(result, stage.id, {
    status,
    startedAt: stage.startedAt ?? activity.createdAt,
    endedAt: status === "running" ? null : activity.createdAt,
    detail: text(data.detail),
  });
}

function applyGitOutput(
  result: WorktreeLaunchProgress,
  activity: LaunchActivity,
): WorktreeLaunchProgress {
  const detail = text(record(activity.payload).detail);
  if (!detail) return result;
  const next = { ...result, output: [...result.output, detail.slice(0, 1000)].slice(-20) };
  const match = /Updating files:\s+(\d+)%/.exec(detail);
  if (!match || result.stages[1]?.status !== "running") return next;
  const percent = Number(match[1]);
  return percent >= 0 && percent <= 100 ? patchStage(next, "checkout", { percent }) : next;
}

function stopLaunch(
  result: WorktreeLaunchProgress,
  activity: LaunchActivity,
): WorktreeLaunchProgress {
  const failed = activity.kind === "worktree.failed";
  return {
    ...result,
    phase: failed ? "failed" : "cancelled",
    endedAt: activity.createdAt,
    error: failed
      ? (text(record(activity.payload).detail) ?? "Worktree creation failed.")
      : result.error,
    stages: result.stages.map((stage) =>
      stage.status === "running"
        ? {
            ...stage,
            status: failed ? "failed" : "skipped",
            endedAt: activity.createdAt,
            detail: failed ? stage.detail : "Cancelled",
          }
        : stage,
    ),
  };
}

function startAgent(
  result: WorktreeLaunchProgress,
  activity: LaunchActivity,
): WorktreeLaunchProgress {
  const next =
    record(activity.payload).setupRequested === false
      ? patchStage(result, "setup", { status: "skipped", detail: "Not requested" })
      : result;
  return patchStage(next, "agent", { status: "running", startedAt: activity.createdAt });
}

const ACTIVITY_HANDLERS: Record<
  string,
  (result: WorktreeLaunchProgress, activity: LaunchActivity) => WorktreeLaunchProgress
> = {
  "worktree.stage": applyGitStage,
  "worktree.output": applyGitOutput,
  "worktree.created": (result, activity) => ({
    ...result,
    worktreePath: text(record(activity.payload).worktreePath),
  }),
  "setup-script.requested": (result, activity) =>
    patchStage(result, "setup", { status: "running", startedAt: activity.createdAt }),
  "setup-script.started": (result, activity) => ({
    ...patchStage(result, "setup", {
      status: "done",
      endedAt: activity.createdAt,
      detail: "Running in terminal",
    }),
    terminalId: text(record(activity.payload).terminalId),
  }),
  "setup-script.skipped": (result) =>
    patchStage(result, "setup", { status: "skipped", detail: "Not configured" }),
  // Non-blocking: the setup script starts in a terminal without gating the
  // agent's launch, so a failure here is a warning, not a launch failure —
  // "failed" would read as if the whole worktree setup broke when the agent
  // still starts fine.
  "setup-script.failed": (result, activity) =>
    patchStage(result, "setup", {
      status: "warning",
      endedAt: activity.createdAt,
      detail: text(record(activity.payload).detail),
    }),
  "launch.agent-starting": startAgent,
  "worktree.failed": stopLaunch,
  "launch.cancelled": stopLaunch,
};

function reduceLaunchActivity(
  result: WorktreeLaunchProgress,
  activity: LaunchActivity,
): WorktreeLaunchProgress {
  if (!Object.hasOwn(ACTIVITY_HANDLERS, activity.kind)) return result;
  return ACTIVITY_HANDLERS[activity.kind]?.(result, activity) ?? result;
}

function finishLaunch(
  result: WorktreeLaunchProgress,
  payload: Record<string, unknown>,
  agentStartedAt: string | null,
  stopped: LaunchStop | undefined,
): WorktreeLaunchProgress {
  const turnRequestedAt = text(payload.turnRequestedAt) ?? result.startedAt;
  if (agentStartedAt && Date.parse(agentStartedAt) >= Date.parse(turnRequestedAt)) {
    const endedAt = result.stages[4]?.startedAt ?? agentStartedAt;
    result = patchStage(result, "agent", { status: "done", endedAt });
    result = { ...result, phase: "done", endedAt };
  } else {
    result = applyStoppedTurn(result, stopped);
  }
  return result;
}

function applyStoppedTurn(
  result: WorktreeLaunchProgress,
  stopped: LaunchStop | undefined,
): WorktreeLaunchProgress {
  if (!stopped || Date.parse(stopped.completedAt) < Date.parse(result.startedAt)) return result;
  const detail = stopped.detail?.trim();
  return {
    ...patchStage(result, "agent", {
      status: stopped.state === "error" ? "failed" : "skipped",
      endedAt: stopped.completedAt,
    }),
    phase: stopped.state === "error" ? "failed" : "cancelled",
    endedAt: stopped.completedAt,
    error: stopped.state === "error" && detail ? detail : result.error,
  };
}

export function launchElapsed(
  start: string | null,
  end: string | null,
  now: number,
): string | null {
  if (!start) return null;
  const duration = Math.max(0, (end ? Date.parse(end) : now) - Date.parse(start));
  if (!Number.isFinite(duration)) return null;
  if (duration < 1000) return `${Math.round(duration)}ms`;
  if (duration < 60_000) return `${(duration / 1000).toFixed(1)}s`;
  return `${Math.floor(duration / 60_000)}m ${Math.floor(duration / 1000) % 60}s`;
}
import { decodeCmmdRuntimeWorktreeLaunchActivityKind } from "@cmmd-forge/contracts";
